import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Beaker, Download } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

export default function VitaCyt() {
  const [data, setData] = useState(null);
  const [importing, setImporting] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/vitacyt/poc");
        setData(r.data);
      } catch (err) {
        logger.warn("VitaCyt POC fetch failed:", err?.response?.status);
      }
    })();
  }, []);

  const importPoc = async () => {
    setImporting(true);
    try {
      const { data: doc } = await api.post("/vitacyt/import");
      toast.success("VitaCyt POC imported");
      nav(`/devices/${doc.project_id}`);
    } catch (e) {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  if (!data) return <DashboardLayout><div className="p-10">Loading VitaCyt data…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-5xl">
        <div className="vq-overline mb-2">Connected Ecosystem</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">
          VitaCyt POC Data
        </h1>
        <p className="text-slate-600 mb-8">Live sync from the VitaCyt point-of-care platform.</p>

        <div className="vq-card p-8 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-slate-900 rounded flex items-center justify-center shrink-0">
              <Beaker className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="vq-overline mb-2">{data.version}</div>
              <h2 className="font-display font-bold text-2xl text-slate-900">{data.device}</h2>
              <p className="text-slate-600 mt-2">{data.intended_use}</p>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
              {data.sync_status}
            </span>
          </div>
        </div>

        {/* Test data */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Metric label="Samples processed" value={data.test_data.samples_processed} />
          <Metric label="Sensitivity" value={`${data.test_data.sensitivity_pct}%`} />
          <Metric label="Specificity" value={`${data.test_data.specificity_pct}%`} />
          <Metric label="False positive" value={`${data.test_data.false_positive_rate_pct}%`} />
        </div>

        {/* Regulatory readiness */}
        <div className="vq-card p-6 mb-6">
          <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Regulatory readiness</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(data.regulatory_readiness).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-slate-100 pb-2">
                <span className="text-sm font-mono uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</span>
                <span className="text-sm font-semibold text-slate-900">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Import */}
        <div className="vq-card p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">Seed a new project</h3>
            <p className="text-sm text-slate-600 mt-1">Import the VitaCyt POC into VitaQMS to run classification and generate the QMS.</p>
          </div>
          <Button onClick={importPoc} disabled={importing} data-testid="vitacyt-import-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Download className="w-4 h-4 mr-2" /> {importing ? "Importing…" : "Import to VitaQMS"}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Metric({ label, value }) {
  return (
    <div className="vq-card p-5">
      <div className="vq-overline mb-1">{label}</div>
      <div className="font-display font-extrabold text-2xl text-slate-900">{value}</div>
    </div>
  );
}
